
// MUI
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded';
import { alpha, useTheme } from '@mui/material/styles';

// Custom
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { useState } from 'react';
import { SellSection } from './SellSection/SellSection';
import { Transition } from '../../../animations/Transition';
import { BuySection } from './BuySection/BuySection';

interface BuySellModalProps
{
  open: boolean
  handleClose: () => void
  tradingPair: string
  maxSell: number
  maxBuy?: number
  waitForBearSignal: boolean
  side: "buy" | "sell"
}

export const BuySellModal = ({ open, handleClose, tradingPair, maxSell, maxBuy, waitForBearSignal, side }: BuySellModalProps) =>
{
  const theme = useTheme()
  const [ tabIndex, setTabIndex ] = useState(side)
  return(
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      TransitionComponent={Transition}
      keepMounted
      onClose={handleClose}
      PaperProps={{ sx: styles.dialog(theme) }}
      aria-describedby="alert-dialog-slide-description">
    <DialogTitle sx={styles.titleBar(theme)}>
      <Grid container spacing={2} alignItems="center">
        <Grid item>
          <Box sx={styles.headerIcon(theme)}>
            <SwapHorizRoundedIcon />
          </Box>
        </Grid>
        <Grid item xs>
          <Typography variant="h5" fontWeight={700}>Confirm {tabIndex}</Typography>
          <Typography variant="body2" sx={styles.subtitle(theme)}>
            Review the trade action and confirm the order for {tradingPair}.
          </Typography>
        </Grid>
        <Grid item>
          <IconButton onClick={handleClose} sx={styles.closeButton(theme)}>
            <CloseIcon />
          </IconButton>
        </Grid>
      </Grid>
    </DialogTitle>

    <Tabs value={tabIndex} onChange={(_, index) => setTabIndex(index)} sx={styles.tabs(theme)}>
      <Tab value="buy" label="Buy"></Tab>
      <Tab value="sell" label="Sell"></Tab>
    </Tabs>

    { tabIndex === "sell" && <SellSection handleClose={handleClose} maxSell={maxSell} tradingPair={tradingPair} waitForBearSignal={waitForBearSignal} /> }
    { tabIndex === "buy" && <BuySection handleClose={handleClose} maxBuy={maxBuy} tradingPair={tradingPair} /> }

  </Dialog>
  )
}

const styles = {
  dialog: (theme: any) => ({
    borderRadius: "18px",
    overflow: "hidden",
    background:
      theme.palette.mode === "dark"
        ? `linear-gradient(180deg, ${alpha("#101c2d", 0.98)} 0%, ${alpha("#0a1320", 0.99)} 100%)`
        : `linear-gradient(180deg, ${alpha("#ffffff", 0.98)} 0%, ${alpha("#eef5ff", 0.98)} 100%)`,
    border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.6)}`,
  }),
  titleBar: (theme: any) => ({
    px: 3,
    pt: 3,
    pb: 2,
    borderBottom: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.6)}`,
  }),
  headerIcon: (theme: any) => ({
    width: 44,
    height: 44,
    borderRadius: "12px",
    display: "grid",
    placeItems: "center",
    color: theme.palette.info.main,
    backgroundColor: alpha(theme.palette.info.main, theme.palette.mode === "dark" ? 0.16 : 0.14),
  }),
  subtitle: (theme: any) => ({
    color: alpha(theme.palette.text.primary, 0.68),
  }),
  closeButton: (theme: any) => ({
    width: 40,
    height: 40,
    borderRadius: "12px",
    border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.6)}`,
    backgroundColor: alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.04 : 0.7),
  }),
  tabs: (theme: any) => ({
    px: 2,
    borderBottom: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.5)}`,
    '& .MuiTab-root': {
      textTransform: 'none',
      fontWeight: 700,
    },
  }),
}
